import type { HttpClient } from '~/api/client';
import { httpClient } from '~/api/client';
import type { OAISchema } from '~/types/openapi';

// Response 타입 (shoebill.ts에서 가져온 raw DTO)
type MarketSelectProductListResponse =
  OAISchema['MarketSelectProductResponseV2'][];
type RecentlyViewedProductListResponse =
  OAISchema['MarketRecentlyViewedProductResponse'][];
type MarketProductDetailResponse = OAISchema['MarketProductDetailsResponseV2'];
type ProductOptionListResponse = OAISchema['ProductOptionListResponse'];
type EstimateProductPaymentResponse =
  OAISchema['EstimateProductPaymentResponse'];

// Request 타입 (OAISchema 그대로 사용)
export type EstimateProductPaymentRequest =
  OAISchema['EstimateProductPaymentRequest'];

export interface ProductsDataSource {
  // Query
  getSimilarProducts(offerId: number): Promise<MarketSelectProductListResponse>;
  getCrossSellProducts(
    offerId: number,
  ): Promise<MarketSelectProductListResponse>;
  getRecentlyViewedProducts(): Promise<RecentlyViewedProductListResponse>;
  getProductDetail(offerId: number): Promise<MarketProductDetailResponse>;
  getProductOptions(offerId: number): Promise<ProductOptionListResponse>;
  // Mutation
  estimateProductPayment(
    body: EstimateProductPaymentRequest,
  ): Promise<EstimateProductPaymentResponse>;
}

export class ProductsDataSourceImpl implements ProductsDataSource {
  // eslint-disable-next-line no-useless-constructor
  constructor(private readonly client: HttpClient = httpClient) {}

  async getSimilarProducts(offerId: number) {
    return this.client.request<MarketSelectProductListResponse>({
      url: '/api/market-place/v1/similar/products',
      method: 'get',
      params: { offerId },
    });
  }

  async getCrossSellProducts(offerId: number) {
    return this.client.request<MarketSelectProductListResponse>({
      url: `/api/market-place/v1/cross-sell/${offerId}/products`,
      method: 'get',
    });
  }

  async getRecentlyViewedProducts() {
    return this.client.request<RecentlyViewedProductListResponse>({
      url: '/api/market-place/v1/products/recently-viewed-products',
      method: 'get',
    });
  }

  async getProductDetail(offerId: number) {
    return this.client.request<MarketProductDetailResponse>({
      url: `/api/market-place/v2/products/${offerId}/details`,
      method: 'get',
    });
  }

  async getProductOptions(offerId: number) {
    return this.client.request<ProductOptionListResponse>({
      url: `/api/market-place/v2/products/${offerId}/options`,
      method: 'get',
    });
  }

  async estimateProductPayment(body: EstimateProductPaymentRequest) {
    return this.client.request<
      EstimateProductPaymentResponse,
      EstimateProductPaymentRequest
    >({
      url: '/api/market-place/v2/products/estimate-product-payment',
      method: 'post',
      data: body,
    });
  }
}

export const productsDataSource = new ProductsDataSourceImpl();
